import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Collapse,
    FormControlLabel,
    Switch,
    TextField
} from "@mui/material";
import {
    Contact,
    NotificationRecipientIdType
} from "../../models";
import {
    useMemo,
    useState
} from "react";

import { NotificationChannel } from "../../API";
import { RecipientNotificationSetting } from "../assessmentSchedule/RecipientNotificationSetting";
import { ResourceNotificationSettingDraft } from "../../lib/dashboard/types/ResourceNotificationSettingDraft";
import { useContacts } from "./contexts/ContactContextProvider";
import { useCurrentUser } from "./contexts/CurrentUserContext";

type IssueSubmissionNotificationSettingFormProps = {
    existingIssueSubmissionNotificationSettings: ResourceNotificationSettingDraft;
    onCancel: () => void;
    onSave: (settings: ResourceNotificationSettingDraft) => (void | Promise<void>);
};

type UserOption = {
    id: string;
    name: string;
};

export const IssueSubmissionNotificationSettingForm = (props: IssueSubmissionNotificationSettingFormProps) => {
    const { existingIssueSubmissionNotificationSettings, onCancel, onSave } = props;

    const [searchValue, setSearchValue] = useState<string>("");
    const [draft, setDraft] = useState<ResourceNotificationSettingDraft>(existingIssueSubmissionNotificationSettings);
    const [validationError, setValidationError] = useState<string | undefined>();
    const { contacts } = useContacts();
    const { userId } = useCurrentUser();

    const validateForm = (setting: ResourceNotificationSettingDraft) => {
        setValidationError(undefined);
        if (!setting.enabled) {
            return true;
        }
        if (!setting.recipients || setting.recipients.length === 0) {
            setValidationError("Please add at least one recipient");
            return false;
        }
        if (!setting.recipients.every(recipient => recipient.notificationChannels && recipient.notificationChannels.length > 0)) {
            setValidationError("Please select at least one notification channel for each recipient");
            return false;
        }
        return true;
    };

    const userOptions = useMemo<Array<Contact | UserOption>>(() => {
        const contactOptions = contacts?.filter(contact => draft.recipients?.every(recipient => recipient.recipientId !== contact.id)) ?? [];
        if (!userId || draft.recipients?.some(recipient => recipient.recipientId === userId)) {
            return contactOptions;
        }
        return [
            {
                id: userId,
                name: "You"
            },
            ...contactOptions
        ];
    }, [contacts, draft.recipients, userId]);

    const isOptionDisabled = (option: Contact | UserOption) => {
        if (option.id === userId) {
            return false;
        }
        return !(option as Contact).email && !(option as Contact).phoneNumber;
    };

    const removeAdditionalRecipient = (recipientId: string) => {
        setDraft(prevSetting => {
            return {
                ...prevSetting,
                recipients: prevSetting.recipients?.filter(recipient => recipient.recipientId !== recipientId)
            };
        });
    };

    const toggleNotificationChannel = (recipientId: string, channel: NotificationChannel) => {
        setDraft(prevSetting => {
            const newSetting = {
                ...prevSetting,
                recipients: prevSetting.recipients?.map(prevRecipient => {
                    if (prevRecipient.recipientId !== recipientId) {
                        return prevRecipient;
                    }
                    const newNotificationChannels = prevRecipient.notificationChannels?.includes(channel) ?
                        prevRecipient.notificationChannels?.filter(c => c !== channel) :
                        [...(prevRecipient.notificationChannels ?? []), channel];
                    return {
                        ...prevRecipient,
                        notificationChannels: newNotificationChannels,
                        enabled: newNotificationChannels.length > 0
                    };
                })
            };
            validateForm(newSetting);
            return newSetting;
        });
    };

    return (
        <Box display="flex" flexDirection="column">
            <FormControlLabel
                control={(
                    <Switch
                        checked={!!draft.enabled}
                        onChange={(e) => setDraft(prevSetting => {
                            const newSetting = {
                                ...prevSetting,
                                enabled: e.target.checked
                            };
                            validateForm(newSetting);
                            return newSetting;
                        })}
                        name="notificationEnabled"
                        color="success"
                    />
                )}
                label="Enable notification"
                sx={{ mb: 1 }}
            />
            <Collapse
                in={!!draft.enabled}
            >
                <Autocomplete
                    size="small"
                    fullWidth
                    placeholder="Select contact"
                    options={userOptions}
                    getOptionLabel={(option) => option.name || option.id}
                    onChange={(event, newValue, reason) => {
                        if (reason === "selectOption") {
                            setSearchValue("");
                            setDraft(prevSetting => {
                                return {
                                    ...prevSetting,
                                    recipients: [
                                        ...(prevSetting.recipients ?? []),
                                        {
                                            recipientId: newValue?.id,
                                            recipientIdType: newValue?.id === userId ? NotificationRecipientIdType.USER :
                                                NotificationRecipientIdType.CONTACT
                                        }
                                    ]
                                };
                            });
                        }
                    }}
                    value={null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search contact"
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name} {isOptionDisabled(option) && "(No contact information)"}
                        </li>
                    )}
                    getOptionDisabled={isOptionDisabled}
                    inputValue={searchValue}
                    sx={{ mb: 1 }}
                />
                {draft.recipients && draft.recipients.length > 0 && (
                    draft.recipients.map((recipient, index) => {
                        let recipientName: string | undefined;
                        let disableEmail = true;
                        let disableSMS = true;
                        if (recipient.recipientIdType === NotificationRecipientIdType.USER) {
                            if (recipient.recipientId === userId) {
                                recipientName = "You";
                                disableEmail = false;
                                disableSMS = false;
                            } else {
                                recipientName = "Unknown User";
                            }
                        } else if (recipient.recipientIdType === NotificationRecipientIdType.CONTACT) {
                            const contact = contacts?.find(contact => contact.id === recipient.recipientId);
                            disableEmail = !contact?.email;
                            disableSMS = !contact?.phoneNumber;
                            recipientName = contact?.name ?? undefined;
                        }
                        if (!recipientName) {
                            return null;
                        }
                        return (
                            <RecipientNotificationSetting
                                key={index}
                                index={index}
                                recipient={recipient}
                                recipientName={recipientName}
                                disableEmail={disableEmail}
                                disableSMS={disableSMS}
                                toggleNotificationChannel={toggleNotificationChannel}
                                removeRecipient={removeAdditionalRecipient}
                            />
                        );
                    })
                )}
            </Collapse>
            {validationError && (
                <Alert severity="error">
                    {validationError}
                </Alert>
            )}
            <Box display="flex" gap={1} alignItems="center" justifyContent="space-around" mt={2}>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (!validateForm(draft)) {
                            return;
                        }
                        onSave(draft);
                    }}
                >
                    Save
                </Button>
            </Box>
        </Box >
    );
};
